/**
 * En «Listo» de una base nueva (decisión de Alan del 29/10/2026): si la versión anterior tenía el consentimiento
 * declarado, se pregunta si sigue valiendo. Antes, cada base nueva arrancaba sin declarar y había que preparar todo de
 * nuevo cada mes.
 * - «Sí, sigue valiendo» copia la declaración a esta versión con quien lo confirma y la fecha de hoy (`heredadaDe`):
 *   ésa es la constancia. Se ve antes a cuántos les vuelve «sí» (el criterio del envío).
 * - «No» no declara nada: con esta versión vale sólo lo que dice el archivo, como siempre.
 * Después de contestar, las campañas ya preparadas con la versión anterior, con el link a cada una.
 *
 * Sin declaración anterior (o si el panorama no llega) no muestra nada: «Listo» sigue igual que siempre.
 */

import { useState } from "react";
import { Check, Loader2, ShieldCheck, X } from "lucide-react";
import { baseVentasApi } from "../api";
import type { PanoramaConsentimiento, Version } from "../types";
import { cuandoSubida } from "../logic/base";
import { AlcanceDeclaracion, ChipRespaldo, PreparadasDeNuevo } from "./LoQueCambiaLaDeclaracion";
import { usePedido } from "../../emails/hooks/usePedido";
import { leerError, leerRespuesta, textoError } from "../../wa-oficial/logic/respuesta";

export function DeclaracionEnListo({ versionId }: { versionId: string }) {
  const p = usePedido<PanoramaConsentimiento>(() => baseVentasApi.panoramaConsentimiento(versionId), `panorama:${versionId}`);
  const [respuesta, setRespuesta] = useState<"" | "si" | "no">("");
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  const d = p.dato;
  if (!d?.anterior) return null;
  const anterior = d.anterior;
  const yaHeredada = !!d.declarada && d.declarada.heredadaDe === anterior.versionId;
  // ya tenía su propia declaración (otra pestaña): no hay nada que preguntar
  if (d.declarada && !yaHeredada && !respuesta) return null;

  const heredar = async () => {
    setGuardando(true);
    setError("");
    try {
      const l = leerRespuesta<Version>(await baseVentasApi.heredarConsentimiento(versionId));
      if (!l.ok) {
        setError(textoError(l));
        return;
      }
      setRespuesta("si");
    } catch (e) {
      setError(textoError(leerError(e)));
    } finally {
      setGuardando(false);
    }
  };

  const contestada = respuesta !== "" || yaHeredada;
  return (
    <section className="rounded-2xl border border-slate-200 bg-slate-50 px-5 py-4 space-y-3 text-left" data-testid="declaracion-en-listo" aria-labelledby="pregunta-declaracion">
      {!contestada ? (
        <>
          <h2 id="pregunta-declaracion" className="flex items-start gap-2 text-base font-semibold text-slate-900">
            <ShieldCheck size={18} className="mt-0.5 shrink-0 text-violet-600" aria-hidden="true" />
            <span>¿Sigue valiendo la declaración de la versión anterior («{anterior.como}», {cuandoSubida(anterior.at)})?</span>
          </h2>
          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
            <ChipRespaldo c={anterior} versionId={anterior.versionId} />
            <span>Si decís que sí, queda a tu nombre con la fecha de hoy, como constancia de que el comercio no cambió nada.</span>
          </div>
          <AlcanceDeclaracion alcance={d.alcance} />
          {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={guardando}
              onClick={() => void heredar()}
              className="min-h-[44px] rounded-lg bg-violet-600 px-4 text-sm font-semibold text-white hover:bg-violet-700 inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              {guardando ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <Check size={14} aria-hidden="true" />} Sí, sigue valiendo
            </button>
            <button
              type="button"
              disabled={guardando}
              onClick={() => setRespuesta("no")}
              className="min-h-[44px] rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1.5"
            >
              <X size={14} aria-hidden="true" /> No
            </button>
          </div>
        </>
      ) : respuesta === "no" ? (
        <p role="status" className="text-sm text-slate-700">
          No se declaró nada: con esta versión vale sólo lo que dice el archivo. Si el comercio te lo vuelve a confirmar, declaralo desde el historial de cargas, con su respaldo.
        </p>
      ) : (
        <p role="status" className="text-sm text-emerald-800">
          La declaración sigue valiendo para esta versión («{anterior.como}»).{" "}
          {respuesta === "si" || !d.declarada ? "Quedó a tu nombre, con la fecha de hoy." : `Lo confirmó ${d.declarada.autor || "alguien del equipo"} el ${cuandoSubida(d.declarada.at)}.`}
        </p>
      )}
      {contestada && <PreparadasDeNuevo preparadas={d.preparadas.filter((x) => x.conLaBaseAnterior)} heredada={respuesta !== "no"} />}
    </section>
  );
}
